/**
 * Agent Intelligence — frontend display for agent outcomes and orchestrator.
 * - Outcome metrics: GET /api/agent/outcomes
 * - Summary via Orchestrator: GET /api/agent/orchestrate/:symbol
 */
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Brain, BarChart3, Zap } from 'lucide-react';
import { getApiBase } from '../utils/apiConfig';
import { getRole, canAccessOperations, authHeaders } from '../utils';
import ProvenanceFooter from './ProvenanceFooter';
import NarrativeReadbackBadge, { NarrativeReadbackShape } from './NarrativeReadbackBadge';
import EvidencePassportFooter, { EvidencePassportShape } from './EvidencePassportFooter';
import CompositeBridgePanel, { CompositeBridgeShape } from './CompositeBridgePanel';

export interface AgentOutcomes {
  reports_generated: number;
  feedback_accepted: number;
  feedback_rejected: number;
  feedback_edited: number;
  alerts_delivered: number;
  reports_on_breach: number;
}

interface AgentIntelligenceSectionProps {
  symbol: string;
  /** The page's canonical snapshot hash (evidence_passport.canonical_status_sha256). The orchestrator
   *  runs its own read; if it lands on a NEWER cached snapshot than the page (the aggcache TTL lapsed
   *  between page load and the GET SUMMARY click), we tag the result "newer preview — not the record"
   *  so a reader never sees two canonical composites without knowing which is the page-of-record. */
  pageSnapshotSha?: string | null;
  /** The page's snapshot pin id (live response `snapshot_pin`). Passed to the orchestrator as
   *  ?snapshot=<pin> so it computes against the EXACT snapshot the page shows (durable P0-1). The
   *  "newer preview" hash tag remains only as the fallback for a pin that aged out of retention. */
  pageSnapshotPin?: string | null;
}

const AgentIntelligenceSection: React.FC<AgentIntelligenceSectionProps> = ({ symbol, pageSnapshotSha, pageSnapshotPin }) => {
  const apiBase = getApiBase();
  // Outcome metrics + the "Agent Intelligence" framing are an internal operations surface — hidden on
  // the free/client view. The "Summary via Orchestrator" cycle stays for everyone.
  const showOps = canAccessOperations(getRole());
  const [outcomes, setOutcomes] = useState<AgentOutcomes | null>(null);
  const [outcomesLoading, setOutcomesLoading] = useState(false);
  const [orchestratorSummary, setOrchestratorSummary] = useState<string | null>(null);
  const [orchestratorLoading, setOrchestratorLoading] = useState(false);
  const [orchestratorError, setOrchestratorError] = useState<string | null>(null);
  const [orchestratorReadback, setOrchestratorReadback] = useState<NarrativeReadbackShape | null>(null);
  const [orchestratorPassport, setOrchestratorPassport] = useState<EvidencePassportShape | null>(null);
  const [orchestratorBridge, setOrchestratorBridge] = useState<CompositeBridgeShape | null>(null);
  // Which symbol the currently-held orchestrator result belongs to. Pre-revenue audit P0-15
  // (HF007 CROSS_ASSET_LEAKAGE): this component stays mounted as the user switches assets, so
  // without an explicit owner the previous asset's preview would render under the new asset's page
  // (BNB showing XRP's pillars, pixel-for-pixel). We (a) reset all orchestrator state whenever the
  // symbol changes and (b) only render a result when orchestratorForSymbol === the current symbol.
  const [orchestratorForSymbol, setOrchestratorForSymbol] = useState<string | null>(null);
  // Always-current symbol, readable inside async callbacks without a stale closure (P0-15 in-flight guard).
  const symbolRef = useRef(symbol);
  symbolRef.current = symbol;

  useEffect(() => {
    if (!showOps) return; // no outcome metrics on the free/client view — skip the fetch too
    let cancelled = false;
    setOutcomesLoading(true);
    const url = apiBase ? `${apiBase}/api/agent/outcomes` : '/api/agent/outcomes';
    // The route is AuthMiddleware + adminOnly: send the bearer token (it was sent bare → 401 even for
    // the owner, leaving Outcome metrics empty and a console error on every analysis page).
    fetch(url, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setOutcomes(data);
      })
      .catch(() => {})
      .finally(() => setOutcomesLoading(false));
    return () => { cancelled = true; };
  }, [apiBase]);

  // P0-15: clear every orchestrator preview field the moment the asset changes, so a previous asset's
  // summary/bridge/passport can never paint under a new asset's page while the component stays mounted.
  useEffect(() => {
    setOrchestratorSummary(null);
    setOrchestratorReadback(null);
    setOrchestratorPassport(null);
    setOrchestratorBridge(null);
    setOrchestratorError(null);
    setOrchestratorForSymbol(null);
    setOrchestratorLoading(false);
  }, [symbol]);

  const fetchOrchestratorSummary = () => {
    const forSymbol = symbol; // capture the asset this request is for (P0-15 owner check)
    setOrchestratorError(null);
    setOrchestratorSummary(null);
    setOrchestratorReadback(null);
    setOrchestratorPassport(null);
    setOrchestratorBridge(null);
    setOrchestratorForSymbol(null);
    setOrchestratorLoading(true);
    const pinQ = pageSnapshotPin ? `?snapshot=${encodeURIComponent(pageSnapshotPin)}` : '';
    const url = (apiBase ? `${apiBase}/api/agent/orchestrate/${forSymbol}` : `/api/agent/orchestrate/${forSymbol}`) + pinQ;
    // Send the bearer token so an authenticated Pro caller resolves to full_asset_universe (any asset);
    // free/anonymous omits it and is scoped to the top-10 by the backend (enforceFreeAssetScope).
    fetch(url, { headers: authHeaders() })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(res.status === 404
            ? 'Agent endpoint not found (404). Start the Go backend (e.g. go run .) on the proxy port (see package.json "proxy").'
            : `Request failed: ${res.status} ${text.slice(0, 80)}`);
        }
        return res.json();
      })
      .then((data) => {
        // P0-15: if the user switched assets while this request was in flight, discard it — never let
        // a response for asset X land on asset Y's page.
        if (forSymbol !== symbolRef.current) return;
        if (data && data.error) {
          setOrchestratorError(data.error);
          return;
        }
        setOrchestratorSummary(data?.summary ?? '');
        setOrchestratorReadback(data?.narrative_readback ?? null);
        setOrchestratorPassport(data?.evidence_passport ?? null);
        setOrchestratorBridge(data?.composite_bridge ?? null);
        setOrchestratorForSymbol(forSymbol);
      })
      .catch((err) => setOrchestratorError(err.message || 'Request failed'))
      .finally(() => setOrchestratorLoading(false));
  };

  // Pre-revenue audit P0-1 (tag interim): the orchestrator reads through the shared aggcache, so
  // within the cache TTL it lands on the SAME snapshot as the page. But if the TTL lapsed between page
  // load and the GET SUMMARY click, it recomputes a newer one. Compare canonical hashes; when they
  // differ, tag the result "newer preview — not the record" so two composites are never shown as
  // co-equal. (The durable fix is snapshot-id pinning; this is the honest interim.)
  const stripSha = (s?: string | null) => (s || '').replace(/^sha256:/, '');
  const previewIsNewer = !!(orchestratorPassport?.canonical_status_sha256 && pageSnapshotSha &&
    stripSha(orchestratorPassport.canonical_status_sha256) !== stripSha(pageSnapshotSha));

  // Product access (owner directive 2026-09-18): the "Summary via Orchestrator" block is part of the
  // free + Pro experience, so this section renders for everyone. The operations-only surfaces — the
  // Agent Intelligence heading, Outcome metrics, the second composite-bridge TABLE (the P0-1/P0-15
  // "two composites on one client page" root), the readback gate-class badge and the evidence passport
  // — stay gated on `showOps` inside. Clients get the narrative summary + provenance, never a second
  // canonical bridge. (All hooks above run unconditionally — no early return, per the rules of hooks.)

  return (
    <section id="agent-section" className="py-8 md:py-10 px-4 md:px-10 lg:px-20 bg-[color:var(--c-panel-black)]">
      <div className="max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.06)] bg-[color:var(--c-panel-raised)] p-5 md:p-8"
        >
          {showOps && (<>
          <div className="flex items-center gap-3 mb-6">
            <Brain className="w-6 h-6 text-[color:var(--c-gray-500)]" />
            <h2 className="text-xl font-semibold text-[color:var(--c-gray-100)]">Agent Intelligence</h2>
          </div>
          <p className="text-[color:var(--c-gray-400)] text-sm mb-6">
            Outcome metrics and the one-cycle Report Orchestrator. Four deterministic tools compute every number; a single LLM call then writes the narrative summary from those finished figures — it never produces or overrides a metric. Alert subscriptions and the inbox run on the backend when a monitored threshold is breached.
          </p>

          {/* Outcome metrics */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-[color:var(--c-gray-500)]" />
              <h3 className="text-lg font-semibold text-[color:var(--c-gray-100)]">Outcome metrics</h3>
            </div>
            {outcomesLoading ? (
              <p className="text-[color:var(--c-gray-400)] text-sm">Loading…</p>
            ) : outcomes && (outcomes.reports_generated + outcomes.feedback_accepted + outcomes.feedback_rejected + outcomes.feedback_edited + outcomes.alerts_delivered + outcomes.reports_on_breach) === 0 ? (
              <p className="text-[color:var(--c-gray-400)] text-sm">No orchestrator activity recorded in this environment yet — counters populate as reports are generated and reviewed.</p>
            ) : outcomes ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                <div className="bg-[color:var(--c-panel-black)] rounded-md p-4 border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                  <div className="text-xs text-[color:var(--c-gray-400)] uppercase tracking-wider">Reports</div>
                  <div className="text-xl font-bold text-[color:var(--c-text)]">{outcomes.reports_generated}</div>
                </div>
                <div className="bg-[color:var(--c-panel-black)] rounded-md p-4 border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                  <div className="text-xs text-[color:var(--c-gray-400)] uppercase tracking-wider">Accepted</div>
                  <div className="text-xl font-bold text-[color:var(--c-text)]">{outcomes.feedback_accepted}</div>
                </div>
                <div className="bg-[color:var(--c-panel-black)] rounded-md p-4 border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                  <div className="text-xs text-[color:var(--c-gray-400)] uppercase tracking-wider">Rejected</div>
                  <div className="text-xl font-bold text-[color:var(--c-text)]">{outcomes.feedback_rejected}</div>
                </div>
                <div className="bg-[color:var(--c-panel-black)] rounded-md p-4 border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                  <div className="text-xs text-[color:var(--c-gray-400)] uppercase tracking-wider">Edited</div>
                  <div className="text-xl font-bold text-[color:var(--c-text)]">{outcomes.feedback_edited}</div>
                </div>
                <div className="bg-[color:var(--c-panel-black)] rounded-md p-4 border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                  <div className="text-xs text-[color:var(--c-gray-400)] uppercase tracking-wider">Alerts sent</div>
                  <div className="text-xl font-bold text-[color:var(--c-text)]">{outcomes.alerts_delivered}</div>
                </div>
                <div className="bg-[color:var(--c-panel-black)] rounded-md p-4 border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                  <div className="text-xs text-[color:var(--c-gray-400)] uppercase tracking-wider">On breach</div>
                  <div className="text-xl font-bold text-[color:var(--c-text)]">{outcomes.reports_on_breach}</div>
                </div>
              </div>
            ) : (
              <p className="text-[color:var(--c-gray-400)] text-sm">Metrics unavailable (backend may be offline).</p>
            )}
          </div>
          </>)}

          {/* Orchestrator SUMMARY — free + Pro (owner directive 2026-09-18). Everyone gets the narrative
              summary + provenance; the second composite-bridge TABLE (the P0-1/P0-15 "two composites on
              one client page" root), the readback gate-class badge and the evidence passport stay behind
              `showOps` below, so a client never sees a second canonical bridge. */}
          {(
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-[color:var(--c-gray-500)]" />
              <h3 className="text-lg font-semibold text-[color:var(--c-gray-100)]">Summary via Orchestrator{showOps && <span className="text-[11px] font-normal uppercase tracking-wider text-[color:var(--c-gray-500)]"> · operations preview</span>}</h3>
            </div>
            <p className="text-[color:var(--c-gray-400)] text-sm mb-3">
              One cycle: four deterministic tools — metrics, compliance, risk, and market sentiment (derived from price action, volatility, and volume) — compute the figures, then a single LLM call writes the narrative summary from them. The model formats prose only; it never produces or changes a number. This preview is an operations surface; the client-facing record is the page's own Executive Summary and bridge.
            </p>
            <button
              type="button"
              onClick={fetchOrchestratorSummary}
              disabled={orchestratorLoading}
              className="fabric-btn px-4 disabled:opacity-60"
            >
              {orchestratorLoading ? 'Loading…' : `Get summary for ${symbol.toUpperCase()}`}
            </button>
            {orchestratorError && (
              <p className="mt-3 text-red-400 text-sm">{orchestratorError}</p>
            )}
            {orchestratorSummary && orchestratorForSymbol === symbol && (
              <div className="mt-4 p-4 rounded-md bg-[color:var(--c-panel-black)] border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                {previewIsNewer && (
                  <div className="mb-3 inline-flex items-center gap-2 rounded border border-amber-800/50 bg-amber-950/20 px-2.5 py-1 text-[11px] font-mono text-amber-300" title="This summary was computed from a newer snapshot than the page (the shared cache lapsed between page load and this request). The page and its Executive Summary remain the record; treat these figures as a fresher preview, not the canonical snapshot.">
                    ⚠ newer preview · not the record
                  </div>
                )}
                {/* The readback badge + its violation gate-class list (Template-internal-leaked,
                    selector-disagrees, …) is dev-facing detail. A paying client sees only the honest
                    "Narrative withheld" notice below; the internal gate classes are for the
                    admin/operations view. On the LLM PASS path there's simply no badge for clients —
                    they read the narrative directly. */}
                {showOps && orchestratorReadback && (
                  <div className="mb-3">
                    <NarrativeReadbackBadge readback={orchestratorReadback} />
                  </div>
                )}
                {/* On a readback FAIL we withhold the MODEL'S PROSE (it failed the integrity gates) but
                    STILL show the deterministic computation — because cross-asset contamination is now
                    prevented at the source: the bridge is computed against the page-PINNED snapshot and
                    the result is only rendered when orchestratorForSymbol === the current symbol (see the
                    fetch guard). So the figures below are genuinely THIS asset's, and hiding them removed
                    the operator's whole reason to open the preview. Only the LLM narrative is suppressed;
                    a deterministic one-liner (same argmax/argmin-by-contribution the page uses) stands in
                    for it. The readback badge above lists the violations for the operator. */}
                {orchestratorReadback?.status === 'FAIL' ? (
                  showOps ? (
                  <>
                    {(() => {
                      const all = (orchestratorBridge?.pillars ?? []).filter((p) => p.status !== 'WITHHELD');
                      const meas = all.filter((p) => p.status === 'MEASURED');
                      const ps = meas.length ? meas : all;
                      if (!orchestratorBridge || ps.length === 0) return null;
                      const q = meas.length ? 'measured ' : '';
                      const strong = ps.reduce((a, c) => (c.contribution_pp >= a.contribution_pp ? c : a));
                      const weak = ps.reduce((a, c) => (c.contribution_pp <= a.contribution_pp ? c : a));
                      const pp = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)} pts`;
                      return (
                        <p className="text-[color:var(--c-gray-100)]">
                          {symbol.toUpperCase()} composite {orchestratorBridge.total.toFixed(1)}/100. Strongest {q}driver:{' '}
                          {strong.label} ({strong.score.toFixed(1)}/100, {pp(strong.contribution_pp)}). Primary {q}watch:{' '}
                          {weak.label} ({weak.score.toFixed(1)}/100, {pp(weak.contribution_pp)}). Composed deterministically from the analytics core.
                        </p>
                      );
                    })()}
                    <div className="mt-2 rounded-md border border-amber-800/50 bg-amber-950/20 p-2.5 text-xs text-[color:var(--c-gray-400)]">
                      The model's prose summary was withheld — it did not clear{' '}
                      {orchestratorReadback.violations?.length ?? 0} automated integrity check
                      {(orchestratorReadback.violations?.length ?? 0) === 1 ? '' : 's'}. The figures below are the
                      deterministic computation for {symbol.toUpperCase()} (page-pinned snapshot) and are unaffected.
                    </div>
                  </>
                  ) : (
                    /* Client view on a readback FAIL: no composite figures or bridge here — the page's own
                       Executive Summary stays the single record (P0-1). Just an honest notice. */
                    <div className="mt-2 rounded-md border border-amber-800/50 bg-amber-950/20 p-2.5 text-sm text-[color:var(--c-gray-400)]">
                      A narrative summary could not be generated for {symbol.toUpperCase()} this run — it did not clear the automated integrity checks. The Executive Summary above remains the record.
                    </div>
                  )
                ) : (
                  <p className="text-[color:var(--c-gray-100)] whitespace-pre-wrap">{orchestratorSummary}</p>
                )}
                {showOps && orchestratorBridge && (
                  <div className="mt-4">
                    <CompositeBridgePanel bridge={orchestratorBridge} />
                  </div>
                )}
                {showOps && orchestratorPassport && (
                  <EvidencePassportFooter passport={orchestratorPassport} />
                )}
              </div>
            )}
          </div>
          )}
          <ProvenanceFooter source="SAA analytics core · Report Orchestrator" method="4 deterministic tools + 1 LLM narrative call" />
        </motion.div>
      </div>
    </section>
  );
};

export default AgentIntelligenceSection;
